import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import clsx from 'clsx';
import React, { useState } from 'react';
import { Button } from 'react-bootstrap';

import { CurrentPlanSelection } from '../../utils/backend-api/subscribe';
import { buildQueryString, extractQueryParams } from '../../utils/queryStringHelper';
import { CustomSpinner } from '../BaseUI';
import Disclosure from '../shared/Stripe/Disclosure';
import styles from './styles.module.scss';

export interface PaymentMethodProps {
  buttonText: string;
  currentPlan: CurrentPlanSelection;
  currentTrialEndDate?: string;
  isTrial?: boolean;
  referringSubscriber?: string;
  referringSubscriberGuid?: string;
}

const PaymentMethod = ({
  currentPlan: { plan, price, cycle },
  buttonText,
  currentTrialEndDate,
  isTrial,
  referringSubscriber,
  referringSubscriberGuid,
}: PaymentMethodProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const protocol = window.location.protocol;
  const host = window.location.host;
  const { modal } = extractQueryParams(['modal']);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      setErrorMessage('Still loading Stripe. Please wait a few seconds and then try again.');
      return;
    }

    setIsLoading(true);

    // Trigger form validation and wallet collection
    const { error: submitError } = await elements.submit();
    if (submitError) {
      setErrorMessage(submitError.message);
      setIsLoading(false);
      return;
    }

    const queryString = buildQueryString({
      modal,
      plan,
      guid: referringSubscriberGuid,
    });
    const { error } = await stripe.confirmSetup({
      elements,
      confirmParams: {
        // Can't use READER_URL for testing, so this is used to build URLs dynamically
        return_url: `${protocol}//${host}/payment-status?${queryString}`,
      },
      redirect: 'always',
    });

    setIsLoading(false);
    if (error) {
      setErrorMessage(error.message);
      return;
    }
  };

  return (
    <>
      <div className={styles.subscribePaymentHeading}>
        <div className={styles.subscribePaymentHeadingText}>Payment Information</div>
        <div className={styles.subscribePaymentHeadingLine}></div>
      </div>
      <div id="payment-form">
        <PaymentElement
          onChange={() => setErrorMessage('')}
          options={{
            layout: {
              type: 'tabs',
              defaultCollapsed: false,
            },
          }}
        />
        {errorMessage && <div className={styles.errorMessage}>{errorMessage}</div>}
      </div>
      <div className={styles.totalContainer}>
        <div className={styles.todaysTotal}>Today&apos;s Total</div>
        <div className="tw-text-right tw-flex tw-flex-col">
          <span className={styles.amount}>${isTrial ? '0.00' : price.toFixed(2)}</span>
          {isTrial && (
            <span>
              ${price.toFixed(2)}/{cycle} after free trial
            </span>
          )}
        </div>
      </div>
      {isTrial && (
        <div className={styles.chargeNote}>
          By beginning your trial, we will be sharing your name with {referringSubscriber}, who has
          referred you, and letting them know that you&apos;ve started enjoying The Information.
        </div>
      )}
      <Disclosure />
      <Button
        onClick={handleSubmit}
        disabled={!stripe || isLoading}
        type="submit"
        className={clsx(styles.submitBtn, 'tw-mt-16')}
      >
        {isLoading ? (
          <CustomSpinner
            color="#fff"
            size={10}
            style={{ display: 'flex', justifyContent: 'center' }}
          />
        ) : (
          <>{buttonText}</>
        )}
      </Button>
      {isTrial && (
        <div className={styles.chargeNote}>
          if you cancel before the end of day on {currentTrialEndDate}, you will not be charged.
        </div>
      )}
    </>
  );
};

export default PaymentMethod;
